import { useSyncExternalStore } from "react";

import { RECENTLY_USED_STORAGE_KEY } from "../constants";

interface ColorValue {
  hex: string;
  rgb: string | { r: number; g: number; b: number; a?: number };
}

const getFromStorage = (key: string, fallback: ColorValue[]): ColorValue[] => {
  try {
    const stored = localStorage.getItem(key);

    return stored ? JSON.parse(stored) : fallback;
  } catch {
    return fallback;
  }
};

const setToStorage = (key: string, value: ColorValue[]) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Silently fail if storage is unavailable
  }
};

const createSyncedStore = (storageKey: string) => {
  const listeners = new Set<() => void>();

  const store = {
    value: getFromStorage(storageKey, []),
    getSnapshot: () => store.value,
    getServerSnapshot: () => [] as ColorValue[],
    setValue: (newValue: ColorValue[]) => {
      store.value = newValue;
      setToStorage(storageKey, newValue);
      listeners.forEach(listener => listener());
    },
    subscribe: (callback: () => void) => {
      listeners.add(callback);

      const storageEventHandler = (event: StorageEvent) => {
        if (event.key !== storageKey) return;
        store.value = getFromStorage(storageKey, []);
        listeners.forEach(listener => listener());
      };

      if (listeners.size === 1) {
        window.addEventListener("storage", storageEventHandler);
      }

      return () => {
        listeners.delete(callback);
        if (listeners.size === 0) {
          window.removeEventListener("storage", storageEventHandler);
        }
      };
    },
  };

  return store;
};

const store = createSyncedStore(RECENTLY_USED_STORAGE_KEY);

export const useRecentlyUsedColors = (): [
  ColorValue[],
  (value: ColorValue[]) => void,
] => [
  useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getServerSnapshot
  ),
  store.setValue,
];
